import React from 'react';
import styles from './InvoiceCard.module.css';

const InvoiceCard = ({ invoice, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = currency === 'USD' ? '$' : '₹';
    return symbol + (amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Invoice Details - #{invoice.invoiceNo} (Line {invoice.lineNum})</h2>
          <button onClick={onClose} className={styles.closeButton}>✕</button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Invoice Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Invoice Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Invoice No:</span>
                <span className={styles.value}>{invoice.invoiceNo}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Invoice Date:</span>
                <span className={styles.value}>{formatDate(invoice.invoicePostingDate)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Dispatch Date:</span>
                <span className={styles.value}>{formatDate(invoice.dispatchDate)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Tracking No:</span>
                <span className={styles.value}>{invoice.trackingNo || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Status:</span>
                <span className={`${styles.statusBadge} ${styles[`status${invoice.status?.replace(/\s+/g, '')}`]}`}>
                  {invoice.status}
                </span>
              </div>
            </div>
          </div>

          {/* Sales Order Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Sales Order Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>SO No:</span>
                <span className={styles.value}>{invoice.soNo || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>SO Date:</span>
                <span className={styles.value}>{formatDate(invoice.soDate)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Customer PO No:</span>
                <span className={styles.value}>{invoice.customerPoNo || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Customer Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Customer Code:</span>
                <span className={styles.value}>{invoice.customerCode}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Customer Name:</span>
                <span className={styles.value}>{invoice.customerName}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Contact Person:</span>
                <span className={styles.value}>{invoice.contactPerson || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Contact Code:</span>
                <span className={styles.value}>{invoice.contactPersonCode || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Phone:</span>
                <span className={styles.value}>{invoice.phone || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Email:</span>
                <span className={styles.value}>{invoice.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Geographic Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Geographic Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>State Code:</span>
                <span className={styles.value}>{invoice.stateCode || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Country:</span>
                <span className={styles.value}>{invoice.country || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Region:</span>
                <span className={styles.value}>{invoice.region || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Line Item Details */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Line Item Details</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Line #:</span>
                <span className={styles.value}>{invoice.lineNum}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Item Code:</span>
                <span className={styles.value}>{invoice.itemCode}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Description:</span>
                <span className={styles.value}>{invoice.description}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>CAS No:</span>
                <span className={styles.value}>{invoice.casNo || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Vendor Catalog No:</span>
                <span className={styles.value}>{invoice.vendorCatalogNo || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Pack Size:</span>
                <span className={styles.value}>{invoice.packSize || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Quantity Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Quantity Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Ordered Qty:</span>
                <span className={styles.value}>{invoice.orderedQty}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Open Qty:</span>
                <span className={styles.value}>{invoice.openQty}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Delivered Qty:</span>
                <span className={styles.value}>{invoice.deliveredQty}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Stock Status (Hyd):</span>
                <span className={`${styles.stockBadge} ${invoice.stockStatus === 'In Stock' ? styles.inStock : styles.outOfStock}`}>
                  {invoice.stockStatus}
                </span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Planned Delivery Date:</span>
                <span className={styles.value}>{formatDate(invoice.plannedDeliveryDate)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Mkt Feedback:</span>
                <span className={styles.value}>{invoice.mktFeedback || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Pricing Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Unit Price:</span>
                <span className={styles.value}>{formatCurrency(invoice.unitPrice, invoice.currency)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Line Total:</span>
                <span className={styles.valueHighlight}>{formatCurrency(invoice.lineTotal, invoice.currency)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Discount %:</span>
                <span className={styles.value}>{invoice.discountPercent}%</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Tax %:</span>
                <span className={styles.value}>{invoice.taxPercent}%</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Currency:</span>
                <span className={styles.value}>{invoice.currency}</span>
              </div>
            </div>
          </div>

          {/* Category Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Category Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Category Code:</span>
                <span className={styles.value}>{invoice.categoryCode || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Category Name:</span>
                <span className={styles.value}>{invoice.categoryName || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Sub Group 1:</span>
                <span className={styles.value}>{invoice.subGroup1 || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Batch Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Batch Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Batch No:</span>
                <span className={styles.value}>{invoice.batchNo || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Vendor Batch No:</span>
                <span className={styles.value}>{invoice.vendorBatchNo || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>COA No:</span>
                <span className={styles.value}>{invoice.coaNo || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Financial Summary</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Subtotal:</span>
                <span className={styles.value}>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Tax Total:</span>
                <span className={styles.value}>{formatCurrency(invoice.taxTotal, invoice.currency)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Discount Total:</span>
                <span className={styles.value}>{formatCurrency(invoice.discountTotal, invoice.currency)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Shipping Fee:</span>
                <span className={styles.value}>{formatCurrency(invoice.shippingFee, invoice.currency)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Total Amount:</span>
                <span className={styles.valueHighlight}>{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Payment Terms:</span>
                <span className={styles.value}>{invoice.paymentTerms || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Address Information</h3>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Billing Address:</span>
                <span className={styles.value}>{invoice.billingAddress || 'N/A'}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Shipping Address:</span>
                <span className={styles.value}>{invoice.shippingAddress || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Comments */}
          {invoice.comments && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Comments</h3>
              <div className={styles.commentsBox}>
                {invoice.comments}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.closeBtn}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCard;